import { useNotifyStatus } from "contexts/status";
import { getBase64 } from "helpers";
import { useTenantTranslation } from "hooks/formatters";
import { useIsAdmin, useTenant } from "hooks/settings";
import {
  SaveUploadedFileApiArg,
  UploadAdhocFileApiArg,
  useSaveUploadedFileMutation,
  useUploadAdhocFileMutation,
} from "src/store/api/generatedApi";
import { UploadedFileType } from "./upload/uploadedFileType";

export const useUploadFile = () => {
  const { t } = useTenantTranslation();
  const notifyStatus = useNotifyStatus();
  const [uploadAdhocFile, { isLoading: uploadAdhocFileLoading }] =
    useUploadAdhocFileMutation();
  const [saveUploadedFile, { isLoading: saveUploadedFileLoading }] =
    useSaveUploadedFileMutation();
  const isAdmin = useIsAdmin();
  const tenant = useTenant();

  const notifySuccess = () => {
    notifyStatus({
      text: t("uploadFileSuccess"),
      type: "success",
    });
  };

  const notifyError = () => {
    notifyStatus({
      text: t("failedToUploadFile"),
      type: "error",
    });
  };

  const createFileObject = (file: File, content: string) => ({
    name: file.name,
    updated_at: new Date(file.lastModified).toISOString(),
    content,
  });

  const uploadAdhocFileType = async (file: File, content: string) => {
    const uploadAdhocFileArg: UploadAdhocFileApiArg = {
      tenantName: tenant,
      file: createFileObject(file, content),
    };

    return uploadAdhocFile(uploadAdhocFileArg).unwrap();
  };

  const uploadOtherFileType = async (
    file: File,
    content: string,
    fileType: UploadedFileType
  ) => {
    if (!isAdmin) {
      console.error("Only admins can upload other files");
      return Promise.reject(new Error("Admin access required"));
    }

    const saveUploadedFileArg: SaveUploadedFileApiArg = {
      tenantName: tenant,
      uploadedFileCreate: {
        type: fileType,
        file: createFileObject(file, content),
      },
    };

    return saveUploadedFile(saveUploadedFileArg).unwrap();
  };

  const handleUpload = async (
    fileToUpload: File | undefined,
    fileType: UploadedFileType
  ) => {
    if (!fileToUpload) {
      return;
    }

    const fileContent = await getBase64(fileToUpload);
    if (typeof fileContent !== "string") {
      notifyError();
      return;
    }

    try {
      if (fileType === "adhoc") {
        await uploadAdhocFileType(fileToUpload, fileContent);
      } else {
        await uploadOtherFileType(fileToUpload, fileContent, fileType);
      }
      notifySuccess();
    } catch {
      notifyError();
    }
  };

  return {
    handleUpload,
    uploadLoading: uploadAdhocFileLoading || saveUploadedFileLoading,
  };
};
